<template>
  <div
    v-if="visible"
    class="policy-docs-panel"
    :class="{
      compact,
      embedded,
      'policy-docs-panel--list': listMode,
      'policy-docs-panel--multi': showDocumentList,
      'policy-docs-panel--collapsed': isCollapsed,
    }"
  >
    <button
      v-if="effectiveShowTitle && collapsible"
      type="button"
      class="policy-docs-header policy-docs-header--toggle"
      :aria-expanded="(!isCollapsed).toString()"
      @click="onHeaderClick"
    >
      <div class="policy-docs-header__left">
        <div class="policy-docs-header__icon" aria-hidden="true">
          <v-icon size="20" color="#184679">mdi-folder-outline</v-icon>
        </div>
        <div class="policy-docs-header__copy">
          <h3 class="policy-docs-title">{{ resolvedTitle }}</h3>
          <p v-if="isCollapsed" class="policy-docs-hint">
            {{ collapsedHint }}
          </p>
        </div>
      </div>
      <div class="policy-docs-header__right">
        <span v-if="showReadyBadge" class="policy-docs-badge">
          {{ documentCount > 0 ? `${documentCount} ${documentCount === 1 ? 'file' : 'files'}` : 'Ready' }}
        </span>
        <span v-else-if="!loading && documentCount === 0 && hasLoadedList" class="policy-docs-badge policy-docs-badge--muted">
          None yet
        </span>
        <v-icon size="22" color="#64748B" class="policy-docs-chevron">
          {{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
        </v-icon>
      </div>
    </button>
    <div v-else-if="effectiveShowTitle" class="policy-docs-header">
      <div class="policy-docs-header__left">
        <h3 class="policy-docs-title">{{ resolvedTitle }}</h3>
      </div>
      <div class="policy-docs-header__right">
        <span v-if="showReadyBadge" class="policy-docs-badge">
          {{ documentCount > 0 ? `${documentCount} ready` : 'Ready' }}
        </span>
      </div>
    </div>

    <div v-show="!isCollapsed">
      <div v-if="loading" class="policy-docs-state">
        <v-progress-circular indeterminate color="#00AEEF" size="24" />
        <span>{{ loadingMessage }}</span>
      </div>

      <div v-else-if="error" class="policy-docs-state policy-docs-state--error">
        <v-icon small color="#DC2626">mdi-alert-circle-outline</v-icon>
        <span>{{ error }}</span>
      </div>

      <div v-else-if="!policyNumber" class="policy-docs-state">
        <v-icon small color="#94A3B8">mdi-clock-outline</v-icon>
        <span>Waiting for policy number…</span>
      </div>

      <div
        v-else-if="showDocumentList"
        class="policy-docs-files"
      >
        <div
          v-for="doc in resolvedDocuments"
          :key="doc.id"
          class="policy-doc-card"
        >
          <div class="policy-doc-icon">
            <v-icon size="24" color="#E53E3E">mdi-file-pdf-box</v-icon>
          </div>
          <div class="policy-doc-meta">
            <p class="policy-doc-name">{{ formatDocumentTypeLabel(doc) }}</p>
            <p class="policy-doc-sub">
              <span v-if="doc.actualName">{{ doc.actualName }}</span>
              <span v-if="doc.dateCreated"> · {{ doc.dateCreated }}</span>
            </p>
          </div>
          <div class="policy-doc-actions">
            <button
              type="button"
              class="btn-outline-sm"
              :disabled="isDocBusy(doc.id)"
              @click="viewDoc(doc)"
            >
              <v-icon x-small left>mdi-eye-outline</v-icon>
              View
            </button>
            <button
              type="button"
              class="btn-primary-sm"
              :disabled="isDocBusy(doc.id)"
              @click="downloadDoc(doc)"
            >
              <v-icon x-small left color="white">mdi-download</v-icon>
              {{ isDocBusy(doc.id) ? 'Loading…' : 'Download' }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-else-if="!policyDocument && !hasLoadedList"
        class="policy-docs-state"
        :class="{ 'policy-docs-state--minimal': listMode }"
      >
        <v-icon v-if="!listMode" small color="#94A3B8">mdi-file-clock-outline</v-icon>
        <span>{{ listMode ? 'Document not ready yet.' : waitingMessage }}</span>
        <button v-if="allowRetry" type="button" class="btn-link" @click="$emit('retry')">
          Check again
        </button>
      </div>

      <div
        v-else-if="policyDocument"
        class="policy-doc-card"
      >
        <div class="policy-doc-icon">
          <v-icon size="28" color="#E53E3E">mdi-file-pdf-box</v-icon>
        </div>
        <div class="policy-doc-meta">
          <p class="policy-doc-name">{{ formatDocumentTypeLabel(policyDocument) }}</p>
          <p class="policy-doc-sub">{{ policyDocument.actualName || policyDocument.docType || 'Policy Document' }}</p>
        </div>
        <div class="policy-doc-actions">
          <button type="button" class="btn-outline-sm" :disabled="fileActionLoading" @click="viewDoc(policyDocument)">
            <v-icon x-small left>mdi-eye-outline</v-icon>
            View
          </button>
          <button type="button" class="btn-primary-sm" :disabled="fileActionLoading" @click="downloadDoc(policyDocument)">
            <v-icon x-small left color="white">mdi-download</v-icon>
            {{ fileActionLoading ? 'Loading…' : 'Download' }}
          </button>
        </div>
      </div>

      <div v-else class="policy-docs-state" :class="{ 'policy-docs-state--minimal': listMode }">
        <v-icon v-if="!listMode" small color="#94A3B8">mdi-file-clock-outline</v-icon>
        <span>{{ listMode ? 'Document not ready yet.' : waitingMessage }}</span>
        <button v-if="allowRetry" type="button" class="btn-link" @click="$emit('retry')">
          Check again
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { downloadPolicyDocument, openPolicyDocument } from '@/utils/policyDocumentsApi';

export default {
  name: 'PolicyDocumentsPanel',
  props: {
    policyNumber: { type: String, default: '' },
    policyDocument: { type: Object, default: null },
    /** When provided, list every DocuWare file instead of a single picked document. */
    documents: { type: Array, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    title: { type: String, default: 'Policy document' },
    loadingMessage: { type: String, default: 'Checking for policy document…' },
    waitingMessage: {
      type: String,
      default: 'Policy document is being prepared. It is also sent to email when ready.',
    },
    showTitle: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
    allowRetry: { type: Boolean, default: true },
    /** In policy lists: compact pending state, hide title, lighter chrome. */
    listMode: { type: Boolean, default: false },
    /** When false, render nothing until loading starts or a document exists. */
    showWhenPending: { type: Boolean, default: true },
    /** Collapsible document list (useful on policy detail with many files). */
    collapsible: { type: Boolean, default: false },
    /** Initial open state when collapsible. */
    initiallyExpanded: { type: Boolean, default: true },
  },
  data() {
    return {
      fileActionLoading: false,
      fileActionById: {},
      collapsed: !this.initiallyExpanded,
    };
  },
  computed: {
    resolvedDocuments() {
      return Array.isArray(this.documents) ? this.documents : [];
    },
    hasLoadedList() {
      return Array.isArray(this.documents);
    },
    showDocumentList() {
      return this.hasLoadedList && this.resolvedDocuments.length > 0;
    },
    documentCount() {
      if (this.showDocumentList) return this.resolvedDocuments.length;
      return this.policyDocument ? 1 : 0;
    },
    resolvedTitle() {
      if (this.showDocumentList || this.hasLoadedList) {
        return this.title === 'Policy document' ? 'Policy documents' : this.title;
      }
      return this.title;
    },
    showReadyBadge() {
      return !this.loading && this.documentCount > 0;
    },
    visible() {
      if (this.loading || this.policyDocument || this.error || this.showDocumentList || this.hasLoadedList) {
        return true;
      }
      if (!this.showWhenPending) return false;
      return !!this.policyNumber;
    },
    effectiveShowTitle() {
      return this.showTitle && !this.listMode;
    },
    isCollapsed() {
      return this.collapsible && this.collapsed;
    },
    collapsedHint() {
      if (this.loading) return 'Loading documents…';
      if (this.documentCount > 0) {
        return 'Tap to view Debit Note, Schedule, Policy Document, and more';
      }
      if (this.hasLoadedList) return 'No documents available yet';
      return 'Tap to check for documents';
    },
  },
  watch: {
    initiallyExpanded(value) {
      this.collapsed = !value;
    },
  },
  methods: {
    onHeaderClick() {
      if (!this.collapsible) return;
      this.collapsed = !this.collapsed;
    },
    formatDocumentTypeLabel(doc) {
      const raw = String((doc && (doc.docType || doc.actualName)) || 'Document').trim();
      if (!raw) return 'Document';
      return raw
        .toLowerCase()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    isDocBusy(docId) {
      return !!this.fileActionById[docId] || this.fileActionLoading;
    },
    async viewDoc(doc) {
      if (!doc) return;
      const docId = doc.id;
      this.$set(this.fileActionById, docId, true);
      this.fileActionLoading = true;
      try {
        const result = await openPolicyDocument(doc);
        if (!result.success) {
          if (this.$toast) this.$toast.error(result.message || 'Could not open document');
        } else {
          this.$emit('view', doc);
        }
      } finally {
        this.$delete(this.fileActionById, docId);
        this.fileActionLoading = false;
      }
    },
    async downloadDoc(doc) {
      if (!doc) return;
      const docId = doc.id;
      this.$set(this.fileActionById, docId, true);
      this.fileActionLoading = true;
      try {
        const result = await downloadPolicyDocument(doc, this.policyNumber);
        if (!result.success) {
          if (this.$toast) this.$toast.error(result.message || 'Could not download document');
          return;
        }
        this.$emit('download', doc);
      } finally {
        this.$delete(this.fileActionById, docId);
        this.fileActionLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.policy-docs-panel {
  text-align: left;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 20px;
}
.policy-docs-panel.compact { padding: 14px 16px; }
.policy-docs-panel.embedded.compact {
  padding: 12px 14px;
}

.policy-docs-panel.embedded.compact .policy-docs-state {
  font-size: 0.8rem;
  line-height: 1.45;
}

.policy-docs-panel.embedded {
  background: linear-gradient(135deg, #F8FAFC 0%, #EEF4FB 100%);
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 4px;
  margin-bottom: 0;
  overflow: hidden;
}

.policy-docs-panel.embedded.policy-docs-panel--collapsed {
  padding: 0;
}

.policy-docs-panel.embedded:not(.policy-docs-panel--collapsed) {
  padding: 4px 4px 12px;
  background: #fff;
}

.policy-docs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
}
.policy-docs-header--toggle {
  cursor: pointer;
  border-radius: 12px;
  padding: 14px 14px;
  transition: background 0.15s ease;
}
.policy-docs-header--toggle:hover {
  background: rgba(255, 255, 255, 0.72);
}
.policy-docs-panel.embedded:not(.policy-docs-panel--collapsed) .policy-docs-header--toggle {
  background: linear-gradient(135deg, #F8FAFC 0%, #EEF4FB 100%);
  margin-bottom: 10px;
}
.policy-docs-panel.embedded:not(.policy-docs-panel--collapsed) .policy-docs-header--toggle:hover {
  background: linear-gradient(135deg, #F1F5F9 0%, #E8F0FA 100%);
}
.policy-docs-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.policy-docs-header__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #E2E8F0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(24, 70, 121, 0.06);
}
.policy-docs-header__copy {
  min-width: 0;
}
.policy-docs-header__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.policy-docs-panel.embedded .policy-docs-header:not(.policy-docs-header--toggle) {
  margin-bottom: 10px;
  padding: 10px 12px 10px;
  border-bottom: 1px solid #F1F5F9;
}
.policy-docs-title {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1E4276;
}
.policy-docs-hint {
  margin: 4px 0 0;
  font-size: 0.78rem;
  font-weight: 500;
  color: #64748B;
  line-height: 1.35;
  text-transform: none;
  letter-spacing: 0;
}
.policy-docs-badge {
  font-size: 0.72rem;
  font-weight: 700;
  color: #15803D;
  background: #DCFCE7;
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
}
.policy-docs-badge--muted {
  color: #64748B;
  background: #F1F5F9;
}
.policy-docs-chevron {
  flex-shrink: 0;
}
.policy-docs-state {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748B;
  font-size: 0.84rem;
  line-height: 1.5;
  padding: 4px 10px 8px;
}
.policy-docs-state--error { color: #DC2626; }
.policy-docs-files {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: min(360px, 50vh);
  overflow-y: auto;
  padding: 0 8px;
}
.policy-doc-card {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  background: #FAFBFC;
  border: 1px solid #EEF2F6;
  border-radius: 10px;
  padding: 12px 14px;
}
.policy-docs-panel.embedded .policy-doc-card {
  background: #F8FAFC;
  border: 1px solid #EEF2F6;
  border-radius: 10px;
  padding: 10px 12px;
}
.policy-doc-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #FEF2F2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.policy-doc-meta {
  flex: 1;
  min-width: 0;
}
.policy-doc-name {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1E4276;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.policy-doc-sub {
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: #94A3B8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.policy-doc-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}
@media (max-width: 520px) {
  .policy-doc-card {
    flex-wrap: wrap;
  }
  .policy-doc-actions {
    width: 100%;
    margin-left: 0;
    margin-top: 4px;
  }
  .btn-outline-sm, .btn-primary-sm {
    flex: 1;
    justify-content: center;
  }
  .policy-docs-hint {
    display: none;
  }
}
.btn-outline-sm:disabled, .btn-primary-sm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-outline-sm, .btn-primary-sm, .btn-link {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  padding: 8px 12px;
}
.btn-outline-sm {
  background: white;
  border: 1px solid #CBD5E1;
  color: #1E4276;
}
.btn-primary-sm {
  background: #00AEEF;
  border: none;
  color: white;
}
.policy-docs-state--minimal {
  font-size: 0.78rem;
  gap: 6px;
  flex-wrap: wrap;
}

.policy-docs-panel--list {
  background: transparent;
  border: none;
  padding: 0;
  margin-bottom: 0;
}

.policy-docs-panel--list .policy-doc-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 10px 12px;
}

.btn-link {
  background: none;
  border: none;
  color: #00AEEF;
  padding: 0;
  margin-left: 4px;
}
</style>
